// Darstellung von Linien, Pfeilen, Schlauchleitungen, Flächen und Kreisen.
// Linienartige Elemente sind type 'polyline' mit properties.kind: 'line' | 'arrow' | 'hose'.

export const COLOR_PALETTE = [
  { name: 'Rot', value: '#dc2626' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Gelb', value: '#eab308' },
  { name: 'Grün', value: '#16a34a' },
  { name: 'Blau', value: '#2563eb' },
  { name: 'Hellblau', value: '#0ea5e9' },
  { name: 'Violett', value: '#7c3aed' },
  { name: 'Grau', value: '#6b7280' },
  { name: 'Schwarz', value: '#111827' },
];

export const LINE_WEIGHTS = [
  { key: 'thin', label: 'Dünn', value: 3 },
  { key: 'medium', label: 'Mittel', value: 5 },
  { key: 'thick', label: 'Dick', value: 8 },
];

// Pfeilarten: Farbe, Linienart und Pfeilspitze unterscheiden sich, damit sie auf einen Blick
// auseinanderzuhalten sind (gefüllte Spitze = Weg, offene Spitze = Richtung)
export const ARROW_TYPES = {
  angriff: { label: 'Angriffsweg', color: '#dc2626', dashed: false, head: 'filled' },
  ausbreitung: { label: 'Ausbreitungsrichtung', color: '#ea580c', dashed: true, head: 'open' },
  rueckzug: { label: 'Rückzugsweg', color: '#1d4ed8', dashed: true, head: 'filled' },
  anfahrt: { label: 'Anfahrt', color: '#16a34a', dashed: false, head: 'filled' },
  abfahrt: { label: 'Abfahrt', color: '#6b7280', dashed: true, head: 'filled' },
  wind: { label: 'Windrichtung', color: '#374151', dashed: false, head: 'open' },
  stroemung: { label: 'Strömungsrichtung', color: '#0ea5e9', dashed: false, head: 'open' },
};

export const DEFAULT_ARROW_TYPE = 'angriff';

// Einträge „Richtungen“ und „Schlauchleitung“ im Elementkatalog starten das passende Zeichenwerkzeug
export const ARROW_TYPE_BY_SYMBOL = {
  'pfeil-angriff': 'angriff',
  'pfeil-ausbreitung': 'ausbreitung',
  'pfeil-rueckzug': 'rueckzug',
  'pfeil-anmarsch': 'anfahrt',
  'pfeil-abmarsch': 'abfahrt',
  'pfeil-wind': 'wind',
  'pfeil-strom': 'stroemung',
};
export const HOSE_SYMBOL_ID = 'wasser-schlauch';

export const HOSE_TYPES = {
  B: { label: 'B-Schlauch (20 m)', length: 20 },
  C: { label: 'C-Schlauch (15 m)', length: 15 },
};
export const DEFAULT_HOSE_TYPE = 'B';
const HOSE_COLOR = '#2563eb';

// Werkzeuge, die Punkt für Punkt eine Linie bzw. Fläche zeichnen
export const LINE_TOOLS = ['polyline', 'arrow', 'hose'];
export const isPathTool = (tool) => tool === 'polygon' || LINE_TOOLS.includes(tool);
export const KIND_BY_TOOL = { polyline: 'line', arrow: 'arrow', hose: 'hose' };

// Frühere Standardbezeichnungen werden nicht dauerhaft auf der Karte angezeigt
const DEFAULT_LABELS = ['Linie', 'Fläche'];
export const visibleLabel = (label) => (label && !DEFAULT_LABELS.includes(label) ? label : '');

const weightOf = (properties, fallback) =>
  Number(properties?.weight) > 0 ? Number(properties.weight) : fallback;

// Aufgelöste Darstellung eines Elements; eigene Farbe/Linienart/Stärke haben Vorrang vor der Vorgabe
export const resolveLineStyle = (properties = {}) => {
  if (properties.kind === 'arrow') {
    const type = ARROW_TYPES[properties.arrowType] || ARROW_TYPES[DEFAULT_ARROW_TYPE];
    return {
      color: properties.color || type.color,
      dashed: properties.dashed ?? type.dashed,
      weight: weightOf(properties, 5),
      head: type.head,
    };
  }
  if (properties.kind === 'hose') {
    return {
      color: properties.color || HOSE_COLOR,
      dashed: properties.dashed ?? false,
      weight: weightOf(properties, 5),
      head: null,
    };
  }
  return {
    color: properties.color || '#ef4444',
    dashed: Boolean(properties.dashed),
    weight: weightOf(properties, 3),
    head: null,
  };
};

export const dashArray = (dashed, weight) => (dashed ? `${weight * 2.5}, ${weight * 2}` : undefined);

// Vorschaufarbe beim Zeichnen
export const drawColorFor = (tool, arrowType) => {
  if (tool === 'arrow') return (ARROW_TYPES[arrowType] || ARROW_TYPES[DEFAULT_ARROW_TYPE]).color;
  if (tool === 'hose') return HOSE_COLOR;
  return '#ef4444';
};

// Beschriftung einer Schlauchleitung: Länge und benötigte Schläuche
export const hoseSummary = (properties, lengthMeters) => {
  const hose = HOSE_TYPES[properties.hoseType] || HOSE_TYPES[DEFAULT_HOSE_TYPE];
  const count = Math.max(1, Math.ceil(lengthMeters / hose.length));
  const type = (properties.hoseType || DEFAULT_HOSE_TYPE);
  return `${type}-Leitung ${Math.round(lengthMeters)} m · ${count} Schläuche`;
};
